/**
 * Pickup visuals + placement helpers for WeaponSystem.
 * Icons are built from primitives merged into ONE geometry with vertex colours
 * (1 draw call per icon) and share a single MeshStandardMaterial. The glow is a
 * single additive plane whose canvas texture carries both the soft disc and the ring.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { CityData, WeaponId } from './types';
import type { Rng } from './util';

export type PickupKind = 'weapon' | 'health' | 'armor' | 'cash';

export const PICKUP_COLORS: Record<PickupKind, number> = {
  weapon: 0x5fc8ff,
  health: 0xff4a4a,
  armor: 0x4a7bff,
  cash: 0x5fff7a,
};

// ------------------------------------------------------------- geometry ----

function colored(geo: THREE.BufferGeometry, hex: number): THREE.BufferGeometry {
  const c = new THREE.Color(hex);
  const n = geo.getAttribute('position').count;
  const arr = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    arr[i * 3] = c.r;
    arr[i * 3 + 1] = c.g;
    arr[i * 3 + 2] = c.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(arr, 3));
  // drop attributes that would block merging (uv is kept, everything else stripped)
  for (const name of Object.keys(geo.attributes)) if (name !== 'position' && name !== 'normal' && name !== 'uv' && name !== 'color') geo.deleteAttribute(name);
  return geo;
}

function box(parts: THREE.BufferGeometry[], w: number, h: number, d: number, x: number, y: number, z: number, hex: number, rz = 0, ry = 0): void {
  const g = new THREE.BoxGeometry(w, h, d);
  if (rz) g.rotateZ(rz);
  if (ry) g.rotateY(ry);
  g.translate(x, y, z);
  parts.push(colored(g, hex));
}

function cyl(parts: THREE.BufferGeometry[], rTop: number, rBot: number, len: number, x: number, y: number, z: number, hex: number, alongX = true): void {
  const g = new THREE.CylinderGeometry(rTop, rBot, len, 12, 1);
  if (alongX) g.rotateZ(Math.PI / 2);
  g.translate(x, y, z);
  parts.push(colored(g, hex));
}

// no environment map in the scene: keep metalness low or metals render black
const METAL = 0x4a515c;
const DARK = 0x23262c;
const WOOD = 0x8a5a2b;
const TAN = 0x6b6350;

function buildWeaponParts(id: WeaponId, parts: THREE.BufferGeometry[]): void {
  switch (id) {
    case 'pistol':
      box(parts, 0.36, 0.09, 0.06, 0.03, 0.1, 0, METAL); // slide
      cyl(parts, 0.016, 0.016, 0.08, 0.24, 0.1, 0, DARK); // muzzle
      box(parts, 0.09, 0.21, 0.055, -0.1, -0.05, 0, DARK, 0.28); // grip
      box(parts, 0.1, 0.018, 0.05, -0.02, 0.0, 0, METAL); // trigger guard
      box(parts, 0.05, 0.03, 0.02, 0.15, 0.16, 0, DARK); // sight
      break;
    case 'smg':
      box(parts, 0.44, 0.11, 0.07, 0.0, 0.12, 0, METAL); // body
      cyl(parts, 0.022, 0.022, 0.14, 0.29, 0.13, 0, DARK); // barrel
      box(parts, 0.06, 0.2, 0.05, 0.03, -0.04, 0, DARK, 0.1); // magazine
      box(parts, 0.07, 0.15, 0.05, -0.12, -0.01, 0, DARK, 0.3); // grip
      box(parts, 0.18, 0.03, 0.03, -0.31, 0.15, 0, METAL); // stock top
      box(parts, 0.03, 0.09, 0.05, -0.39, 0.11, 0, METAL); // stock end
      box(parts, 0.1, 0.05, 0.05, 0.08, 0.2, 0, DARK); // top rail block
      break;
    case 'shotgun':
      cyl(parts, 0.024, 0.024, 0.6, 0.18, 0.12, 0, METAL); // barrel
      cyl(parts, 0.018, 0.018, 0.5, 0.16, 0.075, 0, METAL); // tube
      box(parts, 0.16, 0.07, 0.07, 0.14, 0.075, 0, WOOD); // pump
      box(parts, 0.2, 0.1, 0.065, -0.2, 0.1, 0, METAL); // receiver
      box(parts, 0.26, 0.09, 0.055, -0.42, 0.06, 0, WOOD, 0.12); // stock
      box(parts, 0.08, 0.018, 0.05, -0.15, 0.03, 0, METAL); // trigger guard
      break;
    case 'rifle':
      cyl(parts, 0.02, 0.02, 0.34, 0.44, 0.13, 0, METAL); // barrel
      box(parts, 0.24, 0.075, 0.065, 0.16, 0.12, 0, TAN); // handguard
      box(parts, 0.3, 0.1, 0.065, -0.1, 0.12, 0, METAL); // receiver
      box(parts, 0.065, 0.19, 0.05, -0.03, -0.03, 0, DARK, 0.25); // magazine
      box(parts, 0.07, 0.14, 0.05, -0.19, 0.0, 0, DARK, 0.32); // grip
      box(parts, 0.22, 0.075, 0.055, -0.36, 0.11, 0, TAN, 0.06); // stock
      cyl(parts, 0.028, 0.028, 0.16, -0.06, 0.22, 0, DARK); // scope
      box(parts, 0.03, 0.05, 0.03, -0.06, 0.18, 0, METAL); // scope mount
      break;
    case 'bat':
      cyl(parts, 0.04, 0.018, 0.78, 0.0, 0.1, 0, 0xc48a4e); // barrel (thick end +x)
      cyl(parts, 0.019, 0.019, 0.14, -0.4, 0.1, 0, DARK); // grip tape
      cyl(parts, 0.028, 0.028, 0.02, -0.48, 0.1, 0, DARK); // knob
      break;
    default:
      break;
  }
}

function shieldShape(scale: number): THREE.Shape {
  const s = new THREE.Shape();
  const w = 0.23 * scale;
  const t = 0.28 * scale;
  s.moveTo(-w, t);
  s.lineTo(w, t);
  s.lineTo(w, 0.02 * scale);
  s.quadraticCurveTo(w, -0.2 * scale, 0, -0.32 * scale);
  s.quadraticCurveTo(-w, -0.2 * scale, -w, 0.02 * scale);
  s.closePath();
  return s;
}

function buildIconGeometry(kind: PickupKind, weapon?: WeaponId): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  if (kind === 'weapon' && weapon) buildWeaponParts(weapon, parts);
  else if (kind === 'health') {
    box(parts, 0.44, 0.44, 0.06, 0, 0, 0, 0xf4f4f4); // white tile
    box(parts, 0.34, 0.12, 0.08, 0, 0, 0, 0xe8202c);
    box(parts, 0.12, 0.34, 0.08, 0, 0, 0, 0xe8202c);
  } else if (kind === 'armor') {
    const outer = new THREE.ExtrudeGeometry(shieldShape(1), { depth: 0.06, bevelEnabled: true, bevelThickness: 0.015, bevelSize: 0.015, bevelSegments: 2 });
    outer.translate(0, 0, -0.03);
    parts.push(colored(outer, 0x2f6fe6));
    const inner = new THREE.ExtrudeGeometry(shieldShape(0.62), { depth: 0.03, bevelEnabled: false });
    inner.translate(0, 0.01, 0.03);
    parts.push(colored(inner, 0x9cc4ff));
  } else {
    // cash: two banded bundles stacked
    box(parts, 0.34, 0.13, 0.2, 0, -0.07, 0, 0x2f9e4f);
    box(parts, 0.1, 0.135, 0.205, 0.02, -0.07, 0, 0xf0e8c8);
    box(parts, 0.34, 0.13, 0.2, 0.03, 0.07, 0.02, 0x37b25a, 0, 0.35);
    box(parts, 0.1, 0.135, 0.205, 0.0, 0.07, 0.02, 0xf0e8c8, 0, 0.35);
  }
  const merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  if (!merged) return new THREE.BoxGeometry(0.3, 0.3, 0.3);
  merged.computeBoundingSphere();
  return merged;
}

// ---------------------------------------------------------------- caches ----

let iconMat: THREE.MeshStandardMaterial | null = null;
const iconGeos = new Map<string, THREE.BufferGeometry>();
let glowTex: THREE.CanvasTexture | null = null;
const glowMats = new Map<PickupKind, THREE.MeshBasicMaterial>();
let glowGeo: THREE.PlaneGeometry | null = null;

function getIconMaterial(): THREE.MeshStandardMaterial {
  if (!iconMat) iconMat = new THREE.MeshStandardMaterial({ vertexColors: true, metalness: 0.15, roughness: 0.5, emissive: 0x0b0d12, emissiveIntensity: 1 });
  return iconMat;
}

export function getIconGeometry(kind: PickupKind, weapon?: WeaponId): THREE.BufferGeometry {
  const key = kind === 'weapon' ? 'w:' + weapon : kind;
  let g = iconGeos.get(key);
  if (!g) {
    g = buildIconGeometry(kind, weapon);
    iconGeos.set(key, g);
  }
  return g;
}

function getGlowTexture(): THREE.CanvasTexture {
  if (glowTex) return glowTex;
  const s = 256;
  const cv = document.createElement('canvas');
  cv.width = s;
  cv.height = s;
  const ctx = cv.getContext('2d')!;
  const c = s / 2;
  // soft disc
  const g = ctx.createRadialGradient(c, c, 0, c, c, c);
  g.addColorStop(0, 'rgba(255,255,255,0.9)');
  g.addColorStop(0.3, 'rgba(255,255,255,0.35)');
  g.addColorStop(0.62, 'rgba(255,255,255,0.08)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, s, s);
  // crisp ring
  ctx.strokeStyle = 'rgba(255,255,255,0.95)';
  ctx.lineWidth = 7;
  ctx.beginPath();
  ctx.arc(c, c, c * 0.66, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = 16;
  ctx.beginPath();
  ctx.arc(c, c, c * 0.66, 0, Math.PI * 2);
  ctx.stroke();
  glowTex = new THREE.CanvasTexture(cv);
  glowTex.colorSpace = THREE.SRGBColorSpace;
  return glowTex;
}

export function makeGlow(kind: PickupKind): THREE.Mesh {
  if (!glowGeo) {
    glowGeo = new THREE.PlaneGeometry(1.9, 1.9);
    glowGeo.rotateX(-Math.PI / 2);
  }
  let mat = glowMats.get(kind);
  if (!mat) {
    const col = new THREE.Color(PICKUP_COLORS[kind]).multiplyScalar(1.9); // > 1 => bloom
    mat = new THREE.MeshBasicMaterial({ map: getGlowTexture(), color: col, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, fog: false, toneMapped: false });
    glowMats.set(kind, mat);
  }
  const m = new THREE.Mesh(glowGeo, mat);
  m.position.y = 0.04;
  m.renderOrder = 5;
  return m;
}

export function makeIcon(kind: PickupKind, weapon?: WeaponId): THREE.Mesh {
  const m = new THREE.Mesh(getIconGeometry(kind, weapon), getIconMaterial());
  m.castShadow = true;
  m.position.y = 1;
  return m;
}

// ------------------------------------------------------------- placement ----

/**
 * Deterministic sidewalk spots: best-candidate sampling on the walk graph, at
 * least `minDist` apart. `first` (optional) forces the first spot near a point.
 */
export function pickSpots(rng: Rng, city: CityData, count: number, minDist: number, first?: { x: number; z: number }): { x: number; z: number }[] {
  const lanes = city.walkGraph.lanes;
  const spots: { x: number; z: number }[] = [];
  const b = city.bounds;
  const minD2 = minDist * minDist;
  const nearestD2 = (x: number, z: number) => {
    let best = Infinity;
    for (const s of spots) {
      const dx = s.x - x, dz = s.z - z;
      const d = dx * dx + dz * dz;
      if (d < best) best = d;
    }
    return best;
  };
  const sample = (out: { x: number; z: number }): boolean => {
    if (lanes.length > 0) {
      const ln = lanes[Math.floor(rng() * lanes.length)];
      const t = 0.1 + rng() * 0.8;
      out.x = ln.a.x + (ln.b.x - ln.a.x) * t;
      out.z = ln.a.z + (ln.b.z - ln.a.z) * t;
    } else {
      // no walk graph (stub city): spread over the bounds
      out.x = b.minX + 20 + rng() * (b.maxX - b.minX - 40);
      out.z = b.minZ + 20 + rng() * (b.maxZ - b.minZ - 40);
    }
    if (city.isWater(out.x, out.z)) return false;
    return true;
  };

  if (first) {
    if (lanes.length > 0) {
      const n = city.walkGraph.nearest(first.x, first.z);
      const ln = lanes[n.id] ?? lanes[0];
      const t = Math.min(0.9, Math.max(0.1, n.t));
      spots.push({ x: ln.a.x + (ln.b.x - ln.a.x) * t, z: ln.a.z + (ln.b.z - ln.a.z) * t });
    } else spots.push({ x: first.x, z: first.z });
  }

  const cand = { x: 0, z: 0 };
  let guard = 0;
  while (spots.length < count && guard++ < count * 60) {
    let bx = 0, bz = 0, bd = -1;
    for (let k = 0; k < 10; k++) {
      if (!sample(cand)) continue;
      const d = nearestD2(cand.x, cand.z);
      if (d > bd) {
        bd = d;
        bx = cand.x;
        bz = cand.z;
      }
    }
    if (bd < 0) continue;
    if (bd >= minD2 || guard > count * 40) spots.push({ x: bx, z: bz });
  }
  return spots;
}
